/**
 * CustomerExcelDialog - 고객 엑셀 업로드/다운로드 다이얼로그
 *
 * 기존 CustomerListPage 내 인라인 엑셀 로직 추출
 */

import { useState, useCallback } from "react";
import {
  Upload,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { customerApi } from "@/api/customer";
import { exportToExcel, CUSTOMER_EXCEL_COLUMNS } from "@/utils/excelExport";
import type { Customer, CustomerFormData } from "@/types/customer";

interface CustomerExcelDialogProps {
  currentPageData: Customer[];
  searchTerm: string;
  showStarredOnly: boolean;
  sortOrder: string;
  selectedCustomers?: Customer[];
}

export function CustomerExcelDialog({
  currentPageData,
  searchTerm,
  showStarredOnly,
  sortOrder,
  selectedCustomers = [],
}: CustomerExcelDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [parsedData, setParsedData] = useState<CustomerFormData[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const buildFilterInfo = useCallback(() => {
    const filterInfo: Record<string, string> = {};
    if (searchTerm) filterInfo["검색어"] = searchTerm;
    if (showStarredOnly) filterInfo["필터"] = "즐겨찾기만";
    return filterInfo;
  }, [searchTerm, showStarredOnly]);

  const handleExcelDownloadSelected = useCallback(() => {
    if (selectedCustomers.length === 0) {
      toast({
        title: "선택된 고객 없음",
        description: "다운로드할 고객을 먼저 선택해주세요.",
        variant: "destructive",
      });
      return;
    }
    exportToExcel({
      data: selectedCustomers,
      columns: CUSTOMER_EXCEL_COLUMNS,
      fileName: "고객목록_선택",
      sheetName: "고객관리",
      filterInfo: { "다운로드 모드": `선택 (${selectedCustomers.length}건)` },
    });
    toast({
      title: "다운로드 완료",
      description: `선택한 ${selectedCustomers.length}명의 고객 정보가 다운로드되었습니다.`,
    });
  }, [selectedCustomers, toast]);

  const handleExcelDownloadPage = useCallback(() => {
    if (currentPageData.length === 0) {
      toast({
        title: "다운로드할 데이터 없음",
        description: "조회된 고객 정보가 없습니다.",
        variant: "destructive",
      });
      return;
    }
    const filterInfo = buildFilterInfo();
    filterInfo["다운로드 모드"] = `현재 페이지 (${currentPageData.length}건)`;
    exportToExcel({
      data: currentPageData,
      columns: CUSTOMER_EXCEL_COLUMNS,
      fileName: "고객목록_페이지",
      sheetName: "고객관리",
      filterInfo: Object.keys(filterInfo).length > 0 ? filterInfo : undefined,
    });
    toast({
      title: "다운로드 완료",
      description: `${currentPageData.length}명의 고객 정보가 다운로드되었습니다.`,
    });
  }, [currentPageData, buildFilterInfo, toast]);

  const handleExcelDownloadAll = useCallback(async () => {
    setIsExporting(true);
    try {
      const allDataResult = await customerApi.getCustomers({
        page: 0,
        size: 10000,
        q: searchTerm || undefined,
        starred: showStarredOnly || undefined,
        sort: sortOrder,
      });
      const allData = allDataResult?.content || [];
      if (allData.length === 0) {
        toast({
          title: "다운로드할 데이터 없음",
          description: "조회된 고객 정보가 없습니다.",
          variant: "destructive",
        });
        return;
      }
      const filterInfo = buildFilterInfo();
      filterInfo["다운로드 모드"] = `전체 (${allData.length}건)`;
      exportToExcel({
        data: allData,
        columns: CUSTOMER_EXCEL_COLUMNS,
        fileName: "고객목록_전체",
        sheetName: "고객관리",
        filterInfo: Object.keys(filterInfo).length > 0 ? filterInfo : undefined,
      });
      toast({
        title: "다운로드 완료",
        description: `${allData.length}명의 고객 정보가 다운로드되었습니다.`,
      });
    } catch {
      toast({
        title: "다운로드 실패",
        description: "데이터를 불러오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  }, [searchTerm, showStarredOnly, sortOrder, buildFilterInfo, toast]);

  const handleExcelUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploadResult(null);
      setParsedData([]);
      setUploadProgress(0);

      try {
        const XLSX = await import("xlsx");

        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const data = event.target?.result;
            const workbook = XLSX.read(data, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet) as Record<
              string,
              unknown
            >[];

            const customers: CustomerFormData[] = [];
            const errors: string[] = [];

            jsonData.forEach((row, index) => {
              const rowNum = index + 2;
              const name = String(
                row["이름"] || row["고객명"] || row["name"] || "",
              ).trim();
              const phone = String(
                row["전화번호"] || row["연락처"] || row["phone"] || "",
              )
                .trim()
                .replace(/[^0-9]/g, "");

              if (!name) {
                errors.push(`${rowNum}행: 이름이 없습니다.`);
                return;
              }
              if (!phone) {
                errors.push(`${rowNum}행: 전화번호가 없습니다.`);
                return;
              }
              if (phone.length < 9 || phone.length > 11) {
                errors.push(`${rowNum}행: 전화번호 형식이 올바르지 않습니다.`);
                return;
              }

              customers.push({
                name,
                phone,
                birthDate:
                  row["생년월일"] || row["birthDate"]
                    ? String(row["생년월일"] || row["birthDate"]).trim()
                    : undefined,
                note:
                  String(
                    row["메모"] || row["비고"] || row["note"] || "",
                  ).trim() || undefined,
              });
            });

            if (customers.length === 0) {
              toast({
                title: "업로드 실패",
                description:
                  "유효한 고객 데이터가 없습니다. 엑셀 형식을 확인해주세요.",
                variant: "destructive",
              });
              return;
            }

            setParsedData(customers);
            if (errors.length > 0) {
              setUploadResult({
                success: 0,
                failed: 0,
                errors: errors.slice(0, 5),
              });
            }
            toast({
              title: "파일 분석 완료",
              description: `${customers.length}명의 고객 데이터를 찾았습니다. ${errors.length > 0 ? `(${errors.length}건 오류)` : ""}`,
            });
          } catch {
            toast({
              title: "파일 분석 실패",
              description:
                "엑셀 파일을 분석할 수 없습니다. 파일 형식을 확인해주세요.",
              variant: "destructive",
            });
          }
        };
        reader.readAsBinaryString(file);
      } catch {
        toast({
          title: "파일 읽기 실패",
          description: "파일을 읽을 수 없습니다.",
          variant: "destructive",
        });
      }

      e.target.value = "";
    },
    [toast],
  );

  const handleConfirmUpload = useCallback(async () => {
    if (parsedData.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    const errors: string[] = [];
    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < parsedData.length; i++) {
      try {
        await customerApi.createCustomer(parsedData[i]);
        successCount++;
      } catch (err: unknown) {
        failedCount++;
        const errorMsg = err instanceof Error ? err.message : "알 수 없는 오류";
        errors.push(`${parsedData[i].name}: ${errorMsg}`);
      }
      setUploadProgress(Math.round(((i + 1) / parsedData.length) * 100));
    }

    setUploadResult({
      success: successCount,
      failed: failedCount,
      errors: errors.slice(0, 5),
    });
    setIsUploading(false);

    if (successCount > 0) {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast({
        title: "업로드 완료",
        description: `${successCount}명 등록 완료${failedCount > 0 ? `, ${failedCount}명 실패` : ""}`,
      });
    }

    if (failedCount === parsedData.length) {
      toast({
        title: "업로드 실패",
        description: "모든 고객 등록에 실패했습니다.",
        variant: "destructive",
      });
    }
  }, [parsedData, queryClient, toast]);

  return (
    <>
      {/* 다운로드 드롭다운 + 업로드 버튼 */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={isExporting}>
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? "다운로드 중..." : "다운로드"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {selectedCustomers.length > 0 && (
            <DropdownMenuItem onClick={handleExcelDownloadSelected}>
              선택 항목 다운로드 ({selectedCustomers.length}건)
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleExcelDownloadPage}>
            현재 페이지 다운로드
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExcelDownloadAll}>
            전체 다운로드
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
        <Upload className="w-4 h-4 mr-2" />
        업로드
      </Button>

      {/* 업로드 다이얼로그 */}
      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open) {
            setParsedData([]);
            setUploadResult(null);
            setUploadProgress(0);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>고객 엑셀 업로드</DialogTitle>
            <DialogDescription>
              엑셀 파일(.xlsx, .xls)을 업로드하여 고객을 일괄 등록합니다.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label
                htmlFor="excel-file"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="mb-1 text-sm text-muted-foreground">
                    <span className="font-semibold">클릭하여 파일 선택</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    XLSX, XLS 파일 지원
                  </p>
                </div>
                <input
                  id="excel-file"
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={handleExcelUpload}
                  disabled={isUploading}
                />
              </label>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>필수 컬럼:</strong> 이름(또는 고객명), 전화번호(또는
                연락처)
                <br />
                <strong>선택 컬럼:</strong> 생년월일, 메모(또는 비고)
                <br />
                <span className="text-muted-foreground">
                  동일 전화번호 고객이 이미 등록된 경우 건너뜁니다.
                </span>
              </AlertDescription>
            </Alert>

            {parsedData.length > 0 && (
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  <span className="text-sm font-medium">
                    {parsedData.length}명의 고객 데이터 준비됨
                  </span>
                </div>
                <div className="text-xs text-muted-foreground max-h-24 overflow-y-auto">
                  {parsedData.slice(0, 5).map((c, i) => (
                    <div key={i}>
                      {c.name} ({c.phone})
                    </div>
                  ))}
                  {parsedData.length > 5 && (
                    <div className="text-muted-foreground">
                      ... 외 {parsedData.length - 5}명
                    </div>
                  )}
                </div>
              </div>
            )}

            {isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>업로드 중...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}

            {uploadResult && uploadResult.success > 0 && (
              <Alert className="border-success/30 bg-success/10">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <AlertDescription className="text-success">
                  {uploadResult.success}명 등록 완료
                  {uploadResult.failed > 0 && `, ${uploadResult.failed}명 실패`}
                </AlertDescription>
              </Alert>
            )}

            {uploadResult && uploadResult.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="text-xs space-y-1">
                    {uploadResult.errors.map((err, i) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
            >
              닫기
            </Button>
            <Button
              onClick={handleConfirmUpload}
              disabled={parsedData.length === 0 || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  업로드 중...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {parsedData.length}명 등록하기
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
